import { toDecimal, toPercentage } from "../../helpers/PriceShopEngine";
import { Link } from "react-router-dom";
import { CheckCircle, EditIcon, ScrollText, TrashIcon } from "lucide-react";
import React from "react";
import ModalScreen from "../common/ModalScreen";
import ModalScreenProps from "../../model/ModalScreenProps";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import { GridPaginationState } from "@mui/x-data-grid/hooks/features/pagination/gridPaginationInterfaces";
import {
  ManagementCompound,
  NewManagementCompound,
} from "../../model/ManagementCompound";

interface FilterParams {
  search: string;
  page: number;
  per_page: number;
  sort_by: string;
  sort_direction: "asc" | "desc";
  stock_status: string;
}

interface IProps {
  data: ManagementCompound[];
  filters: FilterParams;
  setFilters: (filters: FilterParams) => void;
  totalPages: number;
  totalResults?: number;
  loading: boolean;

  setModalScreen: (modal: ModalScreenProps | null) => void;
  onView: (id: string, e?: React.MouseEvent<HTMLAnchorElement>) => void;
  onEdit: (id: string, e?: React.MouseEvent<HTMLAnchorElement>) => void;
  onDelete: (id: string, e?: React.MouseEvent<HTMLAnchorElement>) => void;
}

const ListItems: React.FC<IProps> = ({
  data,
  filters,
  setFilters,
  totalPages,
  totalResults,
  loading,
  setModalScreen,
  onView,
  onEdit,
  onDelete,
}: IProps) => {
  const openModal = (selectedItem: Compound) => {
    setModalScreen({
      title: `Log/Control de Preço da importação - ${selectedItem.id}`,
      state: "view",
      children: (
        <div className="p-4">
          <p className="text-lg font-semibold mb-2">
            Referência: {selectedItem.id}
          </p>
          <p className="mb-2">Descrição: {selectedItem.description}</p>
          <p className="mb-2 text-sm text-gray-600 justify-items-center">
            <span className="p-2">
              PVP:{" "}
              {toDecimal(selectedItem.check_price().price).toLocaleString(
                "pt-PT",
                {
                  style: "currency",
                  currency: selectedItem.price.currency,
                },
              )}
            </span>
            <span className="p-2">
              Valor esperado:{" "}
              {toDecimal(
                selectedItem.check_price().expected_price,
              ).toLocaleString("pt-PT", {
                style: "currency",
                currency: selectedItem.price.currency,
              })}
            </span>
            <span className="p-2">
              Preço de custo:{" "}
              {toDecimal(selectedItem.check_price().cost_price).toLocaleString(
                "pt-PT",
                {
                  style: "currency",
                  currency: selectedItem.price.currency,
                },
              )}
            </span>
            <span className="p-2">
              Desconto na margem:{" "}
              {toPercentage(selectedItem.check_price().reseller_discount)} %
            </span>
            <span className="p-2">
              Margin recomendada:{" "}
              {toPercentage(selectedItem.check_price().recommended_margin)} %
            </span>
          </p>
          <p className="mb-2">
            Erro na validação:{" "}
            {selectedItem.check_price_mismatch ? (
              <span className="text-red-600 font-semibold ">Sim</span>
            ) : (
              <span className="text-green-600 font-semibold ">Não</span>
            )}
          </p>

          {selectedItem.check_price_log && (
            <div className="mt-4">
              <p className="text-md font-semibold mb-2">Logs/Price Checks:</p>
              <ul>
                {selectedItem.check_price_log.map((log, index) => (
                  <li
                    key={index}
                    className={`mb-1 ${log.has_price_mismatch ? "text-red-600" : "text-green-600"}`}
                  >
                    <span className="font-mono">{log.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ),
      onClose: () => setModalScreen(null),
    });
  };
  const [showListedFields, setShowListedFields] = React.useState<
    Map<string, boolean>
  >(() => {
    const session = sessionStorage.getItem("compounds_listed_fields");
    if (session) {
      return new Map<string, boolean>(JSON.parse(session));
    }
    return new Map<string, boolean>([
      ["categoria", true],
      ["id", true],
      ["description", true],
      ["configured_base_cost_price", true],
      ["configured_base_reseller_price", true],
      ["configured_base_discount_rate", true],
      ["configured_base_discount_value", true],
      ["configured_base_retail_price", true],
      ["configured_reseller_recommended_margin", true],
      ["configured_price_pvp_with_vat", true],
      ["erp_base_cost_price", true],
      ["erp_base_reseller_price", true],
      ["erp_base_retail_price", true],
      ["erp_price_pvp_with_vat", true],
      ["actions", true],
    ]);
  });

  // Save showListedFields to sessionStorage when it changes
  React.useEffect(() => {
    sessionStorage.setItem(
      "compounds_listed_fields",
      JSON.stringify(Array.from(showListedFields.entries())),
    );
  }, [showListedFields]);

  const renderStatus = (status: string) => {
    switch (status.toUpperCase()) {
      case "DRAFT":
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
            Rascunho
          </span>
        );
      case "PENDING":
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
            Pendente
          </span>
        );
      case "PUBLISHED":
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
            Publicado
          </span>
        );
      case "HIDDEN":
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
            Oculto
          </span>
        );
      case "ARCHIVED":
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
            Arquivado
          </span>
        );
      default:
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
            Desconhecido
          </span>
        );
    }
  };

  const columns: GridColDef[] = [
    {
      field: "category_slug",
      headerName: "Categoria",
      valueGetter: (value, row) => row.category_name || "Sem categoria",
      minWidth: 150,
      sortable: true,
    },
    { field: "id", headerName: "Referência", minWidth: 100, sortable: true },
    {
      field: "description",
      headerName: "Descrição",
      flex: 2,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "tags",
      headerName: "Tags",
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        const tags = (params.row as ManagementCompound).tags ?? [];
        if (tags.length === 0) return null;
        return (
          <div className="whitespace-pre-line leading-4 py-1">
            {tags.join("\n")}
          </div>
        );
      },
    },
    {
      field: "configured_base_cost_price",
      headerName: "Preço Custo (Base)",
      minWidth: 70,
      valueGetter: (_, row) =>
        row.getBaseComponentsCostPrice("configured")?.toLocaleString("pt-PT", {
          style: "currency",
          currency: "EUR",
        }),
      cellClassName: "font-mono force-text-right",
      sortable: false,
    },
    {
      field: "configured_base_retail_price",
      headerName: "Preço Rev4 (Base)",
      minWidth: 70,
      valueGetter: (value, row) =>
        row
          .getBaseComponentsResellerPrice("configured")
          ?.toLocaleString("pt-PT", {
            style: "currency",
            currency: "EUR",
          }),
      cellClassName: "font-mono force-text-right",
      sortable: true,
    },
    {
      field: "configured_base_discount_rate",
      headerName: "% Margem",
      minWidth: 70,
      valueGetter: (_, row) =>
        toPercentage(row.getBaseDiscountRate("configured") ?? 0) + " %",
      cellClassName: "font-mono force-text-right",
      sortable: true,
    },
    {
      field: "configured_base_discount_value",
      headerName: "Redução Revenda",
      minWidth: 70,
      valueGetter: (_, row) =>
        row.getBaseDiscountValue("configured")?.toLocaleString("pt-PT", {
          style: "currency",
          currency: "EUR",
        }),
      cellClassName: "font-mono force-text-right",
      sortable: false,
    },
    {
      field: "configured_base_reseller_price",
      headerName: "Preço Loja",
      minWidth: 70,
      valueGetter: (_, row) =>
        row.getBaseComponentsRev4Price("configured")?.toLocaleString("pt-PT", {
          style: "currency",
          currency: "EUR",
        }),
      cellClassName: "font-mono force-text-right",
      sortable: false,
    },
    {
      field: "configured_reseller_recommended_margin",
      headerName: "Margem Recomendada",
      minWidth: 70,
      valueGetter: (value, row) =>
        toPercentage(row.getResellerRecommendedMargin("configured") ?? 0) +
        " %",
      cellClassName: "font-mono force-text-right",
      sortable: true,
    },
    {
      field: "configured_price_pvp_with_vat",
      headerName: "PVP",
      minWidth: 70,
      valueGetter: (value, row) =>
        row.getPricePVPWithVat("configured")?.toLocaleString("pt-PT", {
          style: "currency",
          currency: "EUR",
        }),
      cellClassName: "font-mono force-text-right",
      sortable: true,
    },

    {
      field: "erp_base_cost_price",
      headerName: "ERP - Preço Custo (Base)",
      minWidth: 70,
      valueGetter: (_, row) =>
        row.getBaseComponentsCostPrice("erp")?.toLocaleString("pt-PT", {
          style: "currency",
          currency: "EUR",
        }),
      cellClassName: "font-mono force-text-right erp-data",
      sortable: false,
    },
    {
      field: "erp_base_reseller_price",
      headerName: "ERP - Preço Rev4 (Base)",
      minWidth: 70,
      valueGetter: (_, row) =>
        row.getBaseComponentsRev4Price("erp")?.toLocaleString("pt-PT", {
          style: "currency",
          currency: "EUR",
        }),
      cellClassName: "font-mono force-text-right erp-data",
      sortable: false,
    },
    {
      field: "erp_base_retail_price",
      headerName: "ERP - Preço Loja",
      minWidth: 70,
      valueGetter: (value, row) =>
        row.getBaseComponentsResellerPrice("erp")?.toLocaleString("pt-PT", {
          style: "currency",
          currency: "EUR",
        }),
      cellClassName: "font-mono force-text-right erp-data",
      sortable: true,
    },
    {
      field: "erp_price_pvp_with_vat",
      headerName: "ERP - PVP",
      minWidth: 70,
      valueGetter: (value, row) =>
        row.getPricePVPWithVat("erp")?.toLocaleString("pt-PT", {
          style: "currency",
          currency: "EUR",
        }),
      cellClassName: "font-mono force-text-right erp-data",
      sortable: true,
    },
    {
      field: "difference_cost_price",
      headerName: "Dif Custo",
      minWidth: 80,
      valueGetter: (_, row) => {
        const configuredCost =
          row.getBaseComponentsCostPrice("configured") ?? 0;
        const erpCost = row.getBaseComponentsCostPrice("erp") ?? 0;
        if (erpCost === 0) {
          return "N/A";
        }
        const difference = configuredCost - erpCost;
        return `${difference.toLocaleString("pt-PT", {
          style: "currency",
          currency: "EUR",
        })}`;
      },
      cellClassName: "font-mono force-text-right analytic-data",
      sortable: false,
    },
    {
      field: "difference",
      headerName: "Dif PVP",
      minWidth: 80,
      valueGetter: (_, row) => {
        const configuredPVP = row.getPricePVPWithVat("configured") ?? 0;
        const erpPVP = row.getPricePVPWithVat("erp") ?? 0;
        if (erpPVP === 0) {
          return "N/A";
        }
        const difference = configuredPVP - erpPVP;
        return `${difference.toLocaleString("pt-PT", {
          style: "currency",
          currency: "EUR",
        })}`;
      },
      cellClassName: "font-mono force-text-right analytic-data",
      sortable: false,
    },
    {
      field: "status",
      headerName: "Estado",
      minWidth: 100,
      renderCell: (params) => (
        <div className="flex items-center gap-1">
          {renderStatus(params.value)}
          {(params.row as ManagementCompound).in_campaign && (
            <span
              data-testid="compound-in-campaign"
              title="Em campanha ativa ou agendada"
              className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-100 text-indigo-800"
            >
              Campanha
            </span>
          )}
        </div>
      ),
      sortable: true,
    },
    {
      field: "actions",
      headerName: "",
      width: 150,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => (
        <div>
          {params.row.check_price_mismatch && (
            <Link
              to={`#`}
              aria-label={`Ver ${params.row.name} Logs/Price Checks`}
              onClick={(e) => openModal(params.row)}
            >
              <span className="icon is-medium has-text-grey-light">
                <ScrollText />
              </span>
            </Link>
          )}
          <Link
            to={`/pc/${encodeURIComponent(params.row.id)}`}
            aria-label={`Preview ${params.row.name}`}
            onClick={(e) => onView(params.row.id, e)}
          >
            <span className="icon is-medium has-text-info">
              <CheckCircle />
            </span>
          </Link>
          <Link
            to={`/catalog/compound/${encodeURIComponent(params.row.id)}`}
            aria-label={`Editar ${params.row.name}`}
            onClick={(e) => onEdit(params.row.id, e)}
          >
            <span className="icon is-medium has-text-primary">
              <EditIcon />
            </span>
          </Link>
          <Link
            to={`#`}
            aria-label={`Excluir ${params.row.name}`}
            onClick={(e) => onDelete(params.row.id, e)}
          >
            <span className="icon is-medium has-text-danger">
              <TrashIcon />
            </span>
          </Link>
        </div>
      ),
    },
  ];

  const handlePerPageChange = (perPage: number) => {
    setFilters({
      ...filters,
      per_page: perPage,
      page: 1, // Reset to first page on perPage change
    });
  };

  const handlePageChange = (newPage: number) => {
    setFilters({
      ...filters,
      page: newPage,
    });
  };

  const handleFilterValueChange = (field: string) => {
    return (value: string) =>
      setFilters({
        ...filters,
        [field]: value,
        page: 1, // Reset to first page on filter change
      });
  };

  const handlePaginationChange = (model: {
    page: number;
    pageSize: number;
  }) => {
    if (model.pageSize !== filters.per_page) {
      handlePerPageChange(model.pageSize);
    }
    if (model.page + 1 !== filters.page) {
      handlePageChange(model.page + 1);
    }
  };

  const paginationState: GridPaginationState = {
    paginationMode: "server",
    paginationModel: {
      page: (filters.page ?? 1) - 1,
      pageSize: filters.per_page || 10,
    },
    rowCount: totalResults || -1,
    enabled: true,
    meta: {
      hasNextPage: true,
    },
  };

  return (
    <div className="manage-page">
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <DataGrid
          rows={data.map(NewManagementCompound)}
          columns={columns}
          loading={loading}
          density="standard"
          getRowHeight={() => "auto"}
          initialState={{ pagination: paginationState }}
          rowCount={totalResults || 0}
          paginationMode="server"
          sortingMode="server"
          disableColumnFilter={true}
          onPaginationModelChange={handlePaginationChange}
          onSortModelChange={(model) => {
            if (model.length > 0) {
              const sortModel = model[0];
              setFilters({
                ...filters,
                ["sort_direction"]: sortModel.sort || "asc",
                ["sort_by"]: sortModel.field,
                page: 1, // Reset to first page on filter change
              });
            }
          }}
          columnVisibilityModel={Object.fromEntries(showListedFields)}
          onColumnVisibilityModelChange={(newModel) => {
            const newMap = new Map<string, boolean>(Object.entries(newModel));
            setShowListedFields(newMap);
          }}
          hideFooterSelectedRowCount={true}
          pageSizeOptions={[10, 25, 50, 100]}
          rowSelection={false}
          localeText={{
            columnsManagementSearchTitle: "Procurar",
            columnsManagementNoColumns: "Sem colunas",
            columnsManagementShowHideAllText: "Mostrar/Ocultar Todas",
            columnsManagementReset: "Repor",
            columnsManagementDeleteIconLabel: "Limpar",
            columnMenuLabel: "Menu",
            columnMenuAriaLabel: (columnName: string) =>
              `Menu da coluna ${columnName}`,
            columnMenuShowColumns: "Mostrar colunas",
            columnMenuManageColumns: "Gerir colunas",
            columnMenuFilter: "Filtrar",
            columnMenuHideColumn: "Ocultar coluna",
            columnMenuUnsort: "Remover ordenação",
            columnMenuSortAsc: "Ordenar por ASC",
            columnMenuSortDesc: "Ordenar por DESC",
            columnMenuManagePivot: "Gerir pivot",
            paginationRowsPerPage: "Linhas por página:",
            paginationDisplayedRows: ({ from, to, count, estimated }) => {
              if (!estimated) {
                return `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`;
              }
              const estimatedLabel =
                estimated && estimated > to
                  ? `cerca de ${estimated}`
                  : `mais de ${to}`;
              return `${from}–${to} de ${count !== -1 ? count : estimatedLabel}`;
            },
          }}
          getRowId={(row) => row.id}
        />
      </Paper>
    </div>
  );
};

export default ListItems;
